import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import Papa from "papaparse";
import "./Style.css";
import { BackOfficeHeader } from "./GameSessionHeader";

import {
  FaTimes,
  FaInfo,
  FaChevronDown,
  FaChevronUp,
  FaFileCsv
} from "react-icons/fa";

const getReferralFullName = (item, prefix) =>
  [item?.[`${prefix}_first_name`], item?.[`${prefix}_last_name`]]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" ");

const getReferralDisplay = (item, prefix) => {
  const fullName = getReferralFullName(item, prefix);
  const name = String(item?.[`${prefix}_name`] || fullName || "Unnamed player").trim();
  const showFullName =
    fullName && fullName.toLowerCase().trim() !== name.toLowerCase().trim();

  return { name, fullName, showFullName };
};

const formatReferralBonusAmount = (value) =>
  `$${Number(value || 0).toLocaleString()}`;

export default function Referrals() {
  const { cardroom } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
    const permission = localStorage.getItem("permission");
  const dealerMode = Number(localStorage.getItem("dealer_mode") || 0);
  const [agents, setAgents] = useState([]);
  const [playerReferrals, setPlayerReferrals] = useState([]);
  const [cardroomReferralSettings, setCardroomReferralSettings] = useState({
    referralBonus: 0,
    refereeBonus: 0,
  });
  const [players, setPlayers] = useState([]); // Array of unreferred players for dropdown
  const [currSession, setCurrSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sorting State
  const [agentSort, setAgentSort] = useState("earned");
  const [playerSorts, setPlayerSorts] = useState({});

  // Expanded State
  const [expandedAgents, setExpandedAgents] = useState({});
  const [expandedPlayerReferrals, setExpandedPlayerReferrals] = useState({});
  const [agentPlayers, setAgentPlayers] = useState({}); // { agentId: [players] }

  // Modal State
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
  const [showBonusesModal, setShowBonusesModal] = useState(false);
  const [selectedAgentForModal, setSelectedAgentForModal] = useState(null);
  
  // Specific Data for Modals
  const [agentBonuses, setAgentBonuses] = useState([]);
  
  // Form State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newAgentPlayerId, setNewAgentPlayerId] = useState("");
  
  const [addPlayerReferredId, setAddPlayerReferredId] = useState("");
  const [maxLimit, setMaxLimit] = useState("");
  const [rewardAmount, setRewardAmount] = useState("");

  useEffect(() => {
    if (!token) {
      navigate(`/`);
      return;
    }
    if (dealerMode === 2) {
      navigate(`/${cardroom}/dealer_dashboard`);
      return;
    } else if (!permission?.includes("V")){
      navigate('/');
      return;
    }

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("token");
          navigate(`/${cardroom}/`);
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [cardroom, navigate, dealerMode, permission]);

  const loadData = async () => {
    try {
      const { data: crInfo } = await axios.get(`/${cardroom}/api/cardroom`);
      setCurrSession(crInfo.active);
      setCardroomReferralSettings({
        referralBonus: crInfo.referralBonus || 0,
        refereeBonus: crInfo.refereeBonus || 0,
      });

      const [agentsRes, playersRes, playerReferralsRes] = await Promise.all([
        axios.get(`/${cardroom}/api/agents?sort=${agentSort}`),
        axios.get(`/${cardroom}/api/players/unreferred`),
        axios.get(`/${cardroom}/api/player-referrals/archived`),
      ]);
      setAgents(agentsRes.data);
      setPlayers(playersRes.data);
      setPlayerReferrals(playerReferralsRes.data);
    } catch (err) {
      console.error("Load data failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate(`/`);
      return;
    }
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, cardroom, navigate]);

  // Refetch agents when sort changes
  useEffect(() => {
    if (!token || !cardroom || agents.length === 0) return;
    axios.get(`/${cardroom}/api/agents?sort=${agentSort}`)
      .then(res => setAgents(res.data))
      .catch(err => console.error("Error sorting agents", err));
  }, [agentSort]);


  // Add New Agent
  const handleAddAgent = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!newAgentPlayerId) {
      alert("Please select a player to become an agent");
      return;
    }

    try {
      setIsSubmitting(true);
      await axios.post(`/${cardroom}/api/agents`, {
        playerId: newAgentPlayerId
      });
      setNewAgentPlayerId("");
      loadData(); // Reload to get updated agent list and remove from unreferred dropdown
    } catch (err) {
      alert(err.response?.data?.error || "Could not add agent");
    } finally {
      setIsSubmitting(false);
    }
  };


  const fetchAgentPlayers = async (agentId, sortOpts = playerSorts) => {
    try {
        const sortMode = sortOpts[agentId] || "bonus";
        const { data } = await axios.get(`/${cardroom}/api/agents/${agentId}/players?sort=${sortMode}`);
        setAgentPlayers(prev => ({ ...prev, [agentId]: data }));
    } catch(err) {
        console.error("Could not fetch players for agent", err);
    }
  };

  // Toggle Agent Details
  const toggleAgentDetails = async (agentId) => {
    setExpandedAgents((prev) => ({
      ...prev,
      [agentId]: !prev[agentId],
    }));

    if (!agentPlayers[agentId]) {
        fetchAgentPlayers(agentId, playerSorts);
    }
  };

  const togglePlayerReferralDetails = (referrerPlayerId) => {
    setExpandedPlayerReferrals((prev) => ({
      ...prev,
      [referrerPlayerId]: !prev[referrerPlayerId],
    }));
  };

  const handlePlayerSortChange = (agentId, sortValue) => {
    const newSorts = { ...playerSorts, [agentId]: sortValue };
    setPlayerSorts(newSorts);
    fetchAgentPlayers(agentId, newSorts);
  };


  // Handle Add Player to Agent Modal
  const openAddPlayerModal = (agent) => {
    setSelectedAgentForModal(agent);
    setAddPlayerReferredId("");
    setMaxLimit("");
    setRewardAmount("");
    setShowAddPlayerModal(true);
  };

  const submitAddPlayer = async (e) => {
      e.preventDefault();
      if(isSubmitting) return;

      if(!addPlayerReferredId || !maxLimit || !rewardAmount) {
          alert("Please fill out all fields");
          return;
      }

      try {
          setIsSubmitting(true);
          await axios.post(`/${cardroom}/api/agents/${selectedAgentForModal.id}/players`, {
              referredId: addPlayerReferredId,
              maxLimit: parseInt(maxLimit, 10),
              reward: parseInt(rewardAmount, 10)
          });
          
          setShowAddPlayerModal(false);
          
          // Refresh data
          const { data } = await axios.get(`/${cardroom}/api/agents/${selectedAgentForModal.id}/players`);
          setAgentPlayers(prev => ({ ...prev, [selectedAgentForModal.id]: data }));
          loadData(); // To refresh unreferred players and agents stats
      } catch (err) {
          alert(err.response?.data?.error || "Failed to add player to agent");
      } finally {
          setIsSubmitting(false);
      }
  };

  // View Referral Bonuses Modal
  const openBonusesModal = async (agent) => {
      setSelectedAgentForModal(agent);
      setShowBonusesModal(true);

      try {
          const { data } = await axios.get(`/${cardroom}/api/agents/${agent.id}/bonuses`);
          setAgentBonuses(data);
      } catch(err) {
          console.error("Could not fetch bonuses", err);
          alert("Could not fetch referral bonuses history");
      }
  };


  // Toggle Player Pause/Unpause
  const togglePlayerPause = async (agentId, relId, currentActive) => {
    try {
      if (currentActive === 0) return;
      
      const newActive = currentActive === 1 ? 2 : 1;
      await axios.patch(`/${cardroom}/api/agents/relationships/${relId}/status`, { active: newActive });
      
      setAgentPlayers(prev => {
         const newPlayers = { ...prev };
         const index = newPlayers[agentId].findIndex(p => p.id === relId);
         if (index !== -1) {
             newPlayers[agentId][index].active = newActive;
         }
         return newPlayers;
      });
    } catch (err) {
      alert(err.response?.data?.error || "Failed to update status");
      console.error(err);
    }
  };

  const exportToCSV = (title, headers, data, e) => {
    e.stopPropagation();
    if (!data || data.length === 0) return;
    const csv = Papa.unparse({
      fields: headers,
      data: data
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${title}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalPlayerReferralCount = playerReferrals.reduce(
    (total, referrer) => total + Number(referrer.referred_count || 0),
    0,
  );

  return (
    <div className="game">
      <BackOfficeHeader
        activePage="referrals"
        cardroom={cardroom}
        centerTitle="Referrals"
        dealerMode={dealerMode}
        title="Referrals"
      />
<div className="game-container">
        {loading ? (
          <div className="loading-container" style={{ padding: "4rem", textAlign: "center", color: "white" }}>
            <h2 style={{ marginBottom: "1.5rem", fontWeight: "700" }}>Gathering Data...</h2>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div className="spinner" style={{ border: "4px solid rgba(255,255,255,0.1)", borderTop: "4px solid #6366f1", borderRadius: "50%", width: "50px", height: "50px", animation: "spin 1s linear infinite" }}></div>
            </div>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
          <>
        <div className="referrals-overview-grid referrals-page-columns">
        <div className="referrals-page-column">
        {/* ADD AGENT SECTION */}
        <section className="game-section referrals-overview-grid__agents">
          <h2 className="game-section__title">Make Player an Agent</h2>
          <form
            className="game-form"
            onSubmit={handleAddAgent}
            style={{ display: "flex", alignItems: "center", gap: "1rem" }}
          >
            <div style={{ flex: .33 }}>
              <Select
                required
                isSearchable
                className="game-select"
                classNamePrefix="select"
                options={players.map(p => ({ value: p.id, label: p.name }))}
                value={players.map(p => ({ value: p.id, label: p.name })).find(o => o.value === newAgentPlayerId) || null}
                onChange={(option) => setNewAgentPlayerId(option ? option.value : "")}
                placeholder="Select a player..."
                styles={{
                  control: (base) => ({
                    ...base,
                    backgroundColor: "#2d3748",
                    borderColor: "#4a5568",
                    minWidth: "250px",
                  }),
                  menu: (base) => ({
                    ...base,
                    backgroundColor: "#2d3748",
                  }),
                  option: (base, { isFocused }) => ({
                    ...base,
                    backgroundColor: isFocused ? "#4a5568" : "#2d3748",
                    color: "#e2e8f0",
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: "#e2e8f0",
                  }),
                  input: (base) => ({
                    ...base,
                    color: "#e2e8f0",
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: "#718096",
                  }),
                }}
              />
            </div>
            <button
              type="submit"
              className="game-btn game-btn--primary"
              disabled={isSubmitting || !newAgentPlayerId || !permission?.includes("A")}
            >
              Add Agent
            </button>
          </form>
        </section>


        {/* AGENTS LIST */}
        <section className="game-section referrals-overview-grid__agents">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 className="game-section__title" style={{ margin: 0 }}>Agents ({agents.length})</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.9rem', color: '#a0aec0' }}>Sort by:</span>
              <select className="game-input" style={{ width: 'auto', padding: '0.25rem 2rem 0.25rem 0.5rem', height: 'auto', minHeight: 'unset' }} value={agentSort} onChange={(e) => setAgentSort(e.target.value)}>
                <option value="earned">Earned</option>
                <option value="players">Players</option>
                <option value="payouts">Payouts</option>
              </select>
            </div>
          </div>

          {agents.length === 0 ? (
            <p className="game-empty">No agents registered</p>
          ) : (
            <div className="game-tables">
              {agents.map((agent) => (
                <div key={agent.id} className={`game-table ${expandedAgents[agent.id] ? "game-table--open" : ""}`}>
                  
                  {/* Summary Header */}
                  <div className="game-table__header" onClick={() => toggleAgentDetails(agent.id)}>
                      <div className="game-table__info">
                        <h3 className="game-table__name">{agent.name} </h3>
                        
                        <span className="game-table__stats" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                          Players: {agent.number_of_players_under} &bull; Bonuses Earned: ${agent.total_bonuses} &bull; Payouts: {agent.number_of_bonuses_returned}
                          {expandedAgents[agent.id] && agentPlayers[agent.id]?.length > 0 && (
                            <FaFileCsv
                              className="game-icon-btn game-icon-btn--success"
                              onClick={(e) => {
                                const data = agentPlayers[agent.id].map(rel => ({
                                  Player: rel.name,
                                  "Bonus per Play": rel.reward,
                                  "Total Earned": rel.current_amount,
                                  Limit: rel.max_limit
                                }));
                                exportToCSV(`${agent.name}_Referrals`, Object.keys(data[0] || {}), data, e);
                              }}
                            />
                          )}
                        </span>
                      </div>
                      
                      <div className="game-table__actions hide-on-mobile" onClick={(e) => e.stopPropagation()}>
                         <button className="game-icon-btn" onClick={() => toggleAgentDetails(agent.id)}>
                             {expandedAgents[agent.id] ? <FaChevronUp/> : <FaChevronDown/>}
                         </button>
                      </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedAgents[agent.id] && (
                     <div className="game-table__content">
                         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <h3 style={{ margin: 0 }}>Referred Players</h3>
                                <div className="hide-on-mobile" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <span style={{ fontSize: '0.8rem', color: '#a0aec0' }}>Sort by:</span>
                                  <select className="game-input" style={{ width: 'auto', padding: '0.25rem 2rem 0.25rem 0.5rem', fontSize: '0.8rem', height: 'auto', minHeight: 'unset' }} value={playerSorts[agent.id] || "bonus"} onChange={(e) => { e.stopPropagation(); handlePlayerSortChange(agent.id, e.target.value); }} onClick={(e) => e.stopPropagation()}>
                                    <option value="bonus">Bonus per Play</option>
                                    <option value="progress">Progress %</option>
                                    <option value="total">Total Earned</option>
                                    <option value="limit">Max Limit</option>
                                  </select>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem'}}>
                                <button className="game-btn game-btn--sm game-btn--primary" onClick={(e) => { e.stopPropagation(); openAddPlayerModal(agent); }} disabled={!permission?.includes("A")}>
                                    Add Player
                                </button>
                                                      <button
                                                        className="game-icon-btn game-icon-btn--info"
                                                        onClick={(e) => { e.stopPropagation(); openBonusesModal(agent); }}
                                                        title="View Bonuses Log"
                                                      >
                                                        <FaInfo />
                                                      </button>
                            </div>
                         </div>

                         {(!agentPlayers[agent.id] || agentPlayers[agent.id].length === 0) ? (
                            <p style={{ color: '#666', fontStyle: 'italic', fontSize: '0.9rem' }}>No players referred yet.</p>
                         ) : (
                             <table className="game-table" style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                 <thead>
                                     <tr style={{ borderBottom: '2px solid #eaeaea' }}>
                                         <th style={{ padding: '0.5rem 0' }}>Player</th>
                                         <th>Bonus per Play</th>
                                         <th>Progress</th>
                                         <th>Status</th>
                                         <th>Action</th>
                                     </tr>
                                 </thead>
                                 <tbody>
                                     {agentPlayers[agent.id].map(rel => (
                                         <tr key={rel.id} style={{ borderBottom: '1px solid #eaeaea' }}>
                                             <td style={{ padding: '0.5rem 0', fontWeight: 'bold' }}>{rel.name}</td>
                                             <td>${rel.reward}</td>
                                             <td>
                                                 <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                     <div style={{ flex: 1, backgroundColor: '#eee', height: '8px', borderRadius: '4px', overflow: 'hidden' }}>
                                                        <div style={{ width: `${Math.min(100, (rel.current_amount / rel.max_limit) * 100)}%`, backgroundColor: rel.active === 1 ? '#4ade80' : rel.active === 2 ? '#fbbf24' : '#f87171', height: '100%' }}></div>
                                                     </div>
                                                     <span style={{ fontSize: '0.8rem'}}>
                                                         ${rel.current_amount} / ${rel.max_limit}
                                                     </span>
                                                 </div>
                                             </td>
                                             <td>
                                                 {rel.active === 1 ? (
                                                     <span style={{ color: '#16a34a', fontWeight:'600' }}>Active</span>
                                                 ) : rel.active === 2 ? (
                                                     <span style={{ color: '#f59e0b', fontWeight:'600' }}>Paused</span>
                                                 ) : (
                                                     <span style={{ color: '#dc2626', fontWeight:'600' }}>Capped</span>
                                                 )}
                                             </td>
                                             <td>
                                                 <button
                                                     className={`game-btn game-btn--sm ${rel.active === 1 ? 'game-btn--secondary' : 'game-btn--primary'}`}
                                                     disabled={rel.active === 0 || !permission?.includes("A")}
                                                     onClick={(e) => { e.stopPropagation(); togglePlayerPause(agent.id, rel.id, rel.active); }}
                                                     style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', minHeight: 'unset' }}
                                                 >
                                                     {rel.active === 1 ? 'Pause' : 'Unpause'}
                                                 </button>
                                             </td>
                                         </tr>
                                     ))}
                                 </tbody>
                             </table>
                         )}
                     </div>
                  )}

                </div>
              ))}
            </div>
          )}
        </section>

        </div>
        <div className="referrals-page-column">

        <section className="game-section referrals-bonus-summary">
          <h2 className="game-section__title">Referral Bonus Settings</h2>
          <div className="referrals-bonus-summary__list">
            <div className="referrals-bonus-summary__item">
              <span className="referrals-bonus-summary__label">
                Referral Bonus
              </span>
              <strong className="referrals-bonus-summary__value">
                {formatReferralBonusAmount(
                  cardroomReferralSettings.referralBonus,
                )}
              </strong>
            </div>
            <div className="referrals-bonus-summary__item">
              <span className="referrals-bonus-summary__label">
                Referee Bonus
              </span>
              <strong className="referrals-bonus-summary__value">
                {formatReferralBonusAmount(
                  cardroomReferralSettings.refereeBonus,
                )}
              </strong>
            </div>
          </div>
          <p className="referrals-bonus-summary__note">
            These values can be changed in Settings menu on Admin page
          </p>
        </section>

        {/* PLAYER REFERRALS LIST */}
        <section className="game-section referrals-overview-grid__player-referrals">
          <div className="player-referrals-section__header">
            <h2 className="game-section__title" style={{ margin: 0 }}>
              Player Referrals
            </h2>
            <span className="game-table__stats">
              {totalPlayerReferralCount} total
            </span>
          </div>

          {playerReferrals.length === 0 ? (
            <p className="game-empty">No player referrals recorded</p>
          ) : (
            <div className="game-tables player-referrals-list">
              {playerReferrals.map((referrer) => {
                const referrerDisplay = getReferralDisplay(referrer, "referrer");
                const isExpanded =
                  expandedPlayerReferrals[referrer.referrer_player_id];

                return (
                  <div
                    key={referrer.referrer_player_id}
                    className={`game-table ${isExpanded ? "game-table--open" : ""}`}
                  >
                    <div
                      className="game-table__header player-referral-card__header"
                      onClick={() =>
                        togglePlayerReferralDetails(
                          referrer.referrer_player_id,
                        )
                      }
                    >
                      <div className="game-table__info player-referral-card__info">
                        <div className="player-referral-name-line">
                          <h3 className="game-table__name">
                            {referrerDisplay.name}
                          </h3>
                          {referrerDisplay.showFullName && (
                            <span className="player-referral-full-name">
                              ({referrerDisplay.fullName})
                            </span>
                          )}
                        </div>
                        <span className="game-table__stats">
                          Referred: {referrer.referred_count}
                        </span>
                      </div>

                      <button
                        className="game-icon-btn"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePlayerReferralDetails(
                            referrer.referrer_player_id,
                          );
                        }}
                      >
                        {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                      </button>
                    </div>

                    {isExpanded && (
                      <div className="game-table__content player-referral-card__content">
                        {referrer.referred_players?.length > 0 ? (
                          <div className="player-referral-referred-list">
                            {referrer.referred_players.map((player) => {
                              const referredDisplay = getReferralDisplay(
                                player,
                                "referred",
                              );

                              return (
                                <div
                                  key={player.id}
                                  className="player-referral-referred-item"
                                >
                                  <div className="player-referral-name-line">
                                    <strong>{referredDisplay.name}</strong>
                                    {referredDisplay.showFullName && (
                                      <span className="player-referral-full-name">
                                        ({referredDisplay.fullName})
                                      </span>
                                    )}
                                  </div>
                                  {player.recorded_at && (
                                    <span className="player-referral-date">
                                      {new Date(
                                        player.recorded_at,
                                      ).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="game-empty">
                            No referred players recorded
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
        </div>
        </div>

          </>
        )}
      </div>


      {/* ADD PLAYER MODAL */}
      {showAddPlayerModal && selectedAgentForModal && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setShowAddPlayerModal(false)}
          />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                Add Player under {selectedAgentForModal.name}
              </h3>
            </div>
            
            <div className="game-modal__body">
              <form
                className="game-form-vertical"
                onSubmit={submitAddPlayer}
              >
                <label className="game-label">Select Unreferred Player</label>
                <Select
                  required
                  isSearchable
                  className="game-select"
                  classNamePrefix="select"
                  options={players.map(p => ({ value: p.id, label: p.name }))}
                  value={players.map(p => ({ value: p.id, label: p.name })).find(o => o.value === addPlayerReferredId) || null}
                  onChange={(option) => setAddPlayerReferredId(option ? option.value : "")}
                  placeholder="Select a player..."
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: "#2d3748",
                      borderColor: "#4a5568",
                      minWidth: "100%",
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: "#2d3748",
                    }),
                    option: (base, { isFocused }) => ({
                      ...base,
                      backgroundColor: isFocused ? "#4a5568" : "#2d3748",
                      color: "#e2e8f0",
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "#e2e8f0",
                    }),
                    input: (base) => ({
                      ...base,
                      color: "#e2e8f0",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "#718096",
                    }),
                  }}
                />

                <label className="game-label">Max Lifetime Reward ($ limit)</label>
                <input
                  type="number"
                  className="game-input"
                  placeholder="e.g. 500"
                  value={maxLimit}
                  onChange={(e) => setMaxLimit(e.target.value)}
                  required
                  min="1"
                />

                <label className="game-label">Reward per Session ($)</label>
                <input
                  type="number"
                  className="game-input"
                  placeholder="e.g. 50"
                  value={rewardAmount}
                  onChange={(e) => setRewardAmount(e.target.value)}
                  required
                  min="1"
                />

                <div className="game-modal__footer">
                  <button
                    type="button"
                    className="game-btn game-btn--ghost"
                    onClick={() => setShowAddPlayerModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="game-btn game-btn--primary"
                    disabled={isSubmitting}
                  >
                    Confirm Link
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* BONUSES HISTORY MODAL */}
      {showBonusesModal && selectedAgentForModal && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setShowBonusesModal(false)}
          />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                {selectedAgentForModal.name} Bonus History
              </h3>
              <button
                className="game-icon-btn"
                onClick={() => setShowBonusesModal(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__body game-modal__body--scroll">
              {agentBonuses.length === 0 ? (
                <p className="game-empty">No bonuses recorded yet.</p>
              ) : (
                <div className="game-transaction-list">
                  {agentBonuses.map((b) => {
                    const amt = parseFloat(b.amount).toFixed(0);
                    return (
                      <div key={b.id} className="game-transaction-item">
                        <span className="game-transaction-time">
                          {new Date(b.date).toLocaleTimeString(undefined, {
                            hour: "numeric",
                            minute: "2-digit",
                          })}
                        </span>
                        <span className="game-transaction-amount">
                          {b.referred_player_name}
                        </span>
                        <span className="game-transaction-amount">
                          ${amt}
                        </span>
                        <span className="game-transaction-date">
                          {new Date(b.date).toLocaleDateString(undefined, {
                            month: "numeric",
                            day: "numeric",
                          })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}

    </div>
  );
}
